import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../components/ui/dialog';
import { Upload, FileText, Image, Trash2, Loader2, Download, X, Eye, FolderOpen } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = ["ID", "Proof of Address", "Tax Document", "Credit Report", "Contract", "Other"];

const docTypeBadgeColor = {
  "ID": "bg-blue-100 text-blue-800",
  "Proof of Address": "bg-emerald-100 text-emerald-800",
  "Tax Document": "bg-amber-100 text-amber-800",
  "Credit Report": "bg-purple-100 text-purple-800",
  "Contract": "bg-rose-100 text-rose-800",
  "Other": "bg-slate-100 text-slate-800"
};

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsSection({ clientId, canDelete, userRole }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("Other");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/clients/${clientId}/documents`);
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch documents on mount and when clientId changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDocuments();
  }, [clientId]);

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', docType);
        await axios.post(`${API_URL}/api/clients/${clientId}/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      fetchDocuments();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error uploading document';
      alert(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`);
      fetchDocuments();
      if (previewDoc?.id === docId) {
        setPreviewDoc(null);
        setPreviewUrl(null);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting document');
    }
  };

  const openPreview = (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl(`${API_URL}/api/documents/${doc.id}/file`);
  };

  const closePreview = () => {
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  return (
    <>
      <Card className="bg-white border-slate-200 shadow-sm" data-testid="documents-section">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-slate-400" />
            <CardTitle className="text-lg font-semibold text-slate-900">Documents</CardTitle>
            {documents.length > 0 && (
              <span className="text-sm text-slate-500">({documents.length})</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="w-44 h-9 text-sm" data-testid="doc-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.doc,.docx"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="file-input"
            />
            <Button
              size="sm"
              className="bg-[#1a2e5a] hover:bg-[#15254a] text-white"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-doc-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-lg">
              <FolderOpen className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No documents uploaded yet</p>
              <p className="text-slate-400 text-xs mt-1">Upload images, PDFs, or documents</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="group relative border border-slate-200 rounded-lg overflow-hidden hover:border-[#1a2e5a]/40 hover:shadow-md transition-all cursor-pointer"
                  data-testid={`doc-card-${doc.id}`}
                >
                  {/* Thumbnail */}
                  <div
                    className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden"
                    onClick={() => openPreview(doc)}
                  >
                    {doc.is_image ? (
                      <img
                        src={`${API_URL}/api/documents/${doc.id}/file`}
                        alt={doc.original_filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 p-3">
                        <FileText className="w-10 h-10 text-[#1a2e5a]/40" />
                        <span className="text-xs text-slate-500 font-medium uppercase">
                          {doc.file_extension?.replace('.', '') || 'FILE'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/90 hover:bg-white text-slate-900 shadow-sm text-xs"
                      onClick={() => openPreview(doc)}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                  </div>

                  {/* Info */}
                  <div className="p-2 space-y-1">
                    <p className="text-xs font-medium text-slate-900 truncate" title={doc.original_filename}>
                      {doc.original_filename}
                    </p>
                    <div className="flex items-center justify-between">
                      <Badge className={`text-[10px] px-1.5 py-0 ${docTypeBadgeColor[doc.document_type] || docTypeBadgeColor.Other}`}>
                        {doc.document_type}
                      </Badge>
                      <span className="text-[10px] text-slate-400">{formatFileSize(doc.file_size)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400">
                        {doc.created_at ? format(new Date(doc.created_at), 'MMM d') : ''}
                      </span>
                      <span className="text-[10px] text-slate-500">{doc.uploaded_by_name}</span>
                    </div>
                  </div>

                  {/* Delete button for admin */}
                  {canDelete && (
                    <button
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/80 hover:bg-rose-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                      data-testid={`delete-doc-${doc.id}`}
                    >
                      <Trash2 className="w-3 h-3 text-rose-500" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full-screen preview dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-base">{previewDoc?.original_filename}</DialogTitle>
                <div className="flex items-center gap-3 mt-1">
                  <Badge className={`text-xs ${docTypeBadgeColor[previewDoc?.document_type] || docTypeBadgeColor.Other}`}>
                    {previewDoc?.document_type}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    Uploaded by {previewDoc?.uploaded_by_name}
                    {previewDoc?.created_at && ` on ${format(new Date(previewDoc.created_at), 'MMM d, yyyy')}`}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={previewUrl}
                  download={previewDoc?.original_filename}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm" data-testid="download-doc-btn">
                    <Download className="w-4 h-4 mr-1" /> Download
                  </Button>
                </a>
              </div>
            </div>
          </DialogHeader>
          <div className="flex items-center justify-center bg-slate-50 overflow-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
            {previewDoc?.is_image ? (
              <img
                src={previewUrl}
                alt={previewDoc?.original_filename}
                className="max-w-full max-h-[calc(90vh-80px)] object-contain"
                data-testid="preview-image"
              />
            ) : previewDoc?.file_extension === '.pdf' ? (
              <iframe
                src={previewUrl}
                className="w-full h-[calc(90vh-80px)]"
                title={previewDoc?.original_filename}
                data-testid="preview-pdf"
              />
            ) : (
              <div className="flex flex-col items-center gap-4 py-16">
                <FileText className="w-16 h-16 text-slate-300" />
                <p className="text-slate-500">Preview not available for this file type</p>
                <a href={previewUrl} download={previewDoc?.original_filename} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline">
                    <Download className="w-4 h-4 mr-2" /> Download File
                  </Button>
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
